import { useCallback, useEffect, useRef, useState } from "react";
import { getApiBase, getWsBase } from "../apiBase";

const CHANNEL_LABELS = { whatsapp: "WhatsApp", web: "Web / Embed" };

function isJwtExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

function handleAuthFailure(message) {
  localStorage.removeItem("token");
  window.location.href = `/login?reason=${encodeURIComponent(message || "session_expired")}`;
}

function normalizeTimestamp(ts) {
  if (ts == null || ts === "") return ts;
  if (typeof ts === "number") return ts;
  const s = String(ts).trim();
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) && !/[Zz]$/.test(s) && !/[+-]\d{2}:?\d{2}$/.test(s)) {
    return `${s}Z`;
  }
  return s;
}

function messageTimeMs(ts) {
  if (ts == null || ts === "") return 0;
  if (typeof ts === "number") return ts < 1e12 ? ts * 1000 : ts;
  const d = new Date(normalizeTimestamp(ts));
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function sortMessagesByTime(messages) {
  return [...messages].sort(
    (a, b) => messageTimeMs(a.timestamp) - messageTimeMs(b.timestamp)
  );
}

function mergeMessages(existing, incoming) {
  const byKey = new Map();
  for (const m of [...existing, ...incoming]) {
    const text = (m.text || "").trim();
    if (!text && m.role !== "summary") continue;
    const dedupeKey = `${m.role}|${text}`;
    const prev = byKey.get(dedupeKey);
    if (!prev || messageTimeMs(m.timestamp) >= messageTimeMs(prev.timestamp)) {
      byKey.set(dedupeKey, m);
    }
  }
  return sortMessagesByTime([...byKey.values()]);
}

function replySourceLabel(role) {
  if (role === "agent_reply") return "Human agent";
  if (role === "bot") return "AI · RAG";
  return null;
}

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(
    typeof ts === "number" ? (ts < 1e12 ? ts * 1000 : ts) : normalizeTimestamp(ts)
  );
  return isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function MessageBubble({ msg }) {
  if (msg.role === "summary") {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800 mb-3">
        <span className="font-semibold">Earlier context: </span>{msg.text}
      </div>
    );
  }
  const isUser = msg.role === "user";
  const isHumanAgent = msg.role === "agent_reply";
  const sourceLabel = replySourceLabel(msg.role);
  return (
    <div className={`flex mb-2 ${isUser ? "justify-start" : "justify-end"}`}>
      <div
        className={`max-w-[72%] rounded-2xl px-3 py-2 text-sm break-words ${
          isUser
            ? "bg-[#F3EFF4] text-gray-800"
            : isHumanAgent
            ? "bg-[#5B3A60] text-white"
            : "bg-[#E8F4FD] text-gray-800 border border-[#C5DFF5]"
        }`}
      >
        {!isUser && sourceLabel && (
          <span
            className={`inline-block text-[10px] font-semibold mb-1 px-1.5 py-0.5 rounded ${
              isHumanAgent
                ? "bg-white/20 text-white"
                : "bg-[#5B3A60]/10 text-[#5B3A60]"
            }`}
          >
            {sourceLabel}
          </span>
        )}
        <p className="leading-snug">{msg.text}</p>
        {msg.timestamp && (
          <p className="text-[10px] opacity-50 mt-0.5 text-right">
            {formatTime(msg.timestamp)}
          </p>
        )}
      </div>
    </div>
  );
}

function ConversationCard({ conv, sessionKey, isSelected, onSelect }) {
  const pending = conv.has_pending || (conv.messages || []).some(
    (m) => m.role === "user" && conv.mode === "human"
  );
  return (
    <button
      onClick={() => onSelect(sessionKey)}
      className={`w-full text-left px-4 py-3 border-b border-[#F0EAF1] transition-colors ${
        isSelected ? "bg-[#F3EFF4]" : "hover:bg-gray-50"
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-800 truncate max-w-[60%]">
          {conv.channel === "whatsapp" ? "📱 " : "💻 "}
          {conv.channel === "whatsapp" ? (conv.name || conv.phone || "Session") : (conv.phone || "Session")}
        </span>
        <span
          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
            conv.mode === "human"
              ? "bg-orange-100 text-orange-700"
              : "bg-green-100 text-green-700"
          }`}
        >
          {conv.mode === "human" ? "Human" : "AI"}
        </span>
      </div>
      {conv.channel === "whatsapp" && conv.name && conv.phone && (
        <p className="text-[10px] text-gray-400 truncate mb-0.5">{conv.phone}</p>
      )}
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 truncate max-w-[70%]">
          {conv.last_message || "No messages yet"}
        </span>
        <div className="flex items-center gap-1.5">
          {pending && conv.mode === "human" && (
            <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" title="Waiting for reply" />
          )}
          {conv.unread > 0 && (
            <span className="text-[10px] bg-[#5B3A60] text-white rounded-full px-1.5 py-0.5">
              {conv.unread}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function LiveConversations({ tenantId, botId, isActive = true }) {
  const storageKey = `live_selected_${tenantId}_${botId}`;
  const [conversations, setConversations] = useState({});
  const [selectedKey, setSelectedKey] = useState(null);
  const [channelFilter, setChannelFilter] = useState(() => {
    try {
      return sessionStorage.getItem(`live_channel_${tenantId}_${botId}`) || "web";
    } catch {
      return "web";
    }
  });
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [banner, setBanner] = useState(null);

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isMountedRef = useRef(true);
  const selectedKeyRef = useRef(selectedKey);
  const historyFetchGenRef = useRef(0);

  useEffect(() => {
    selectedKeyRef.current = selectedKey;
  }, [selectedKey]);

  // ── WebSocket ────────────────────────────────────────────────────────────
  const connectWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }
    const token = localStorage.getItem("token") || "";
    const tokenExpired = isJwtExpired(token);
    const url = `${getWsBase()}/ws/agent/${tenantId}/${botId}`;
    if (!token || tokenExpired) {
      setBanner("Session expired. Redirecting to login...");
      handleAuthFailure("session_expired");
      return;
    }
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (wsRef.current !== ws) return;
      ws.send(JSON.stringify({ type: "auth", token }));
      setWsConnected(true);
    };

    ws.onmessage = (e) => {
      if (wsRef.current !== ws) return;
      try {
        const msg = JSON.parse(e.data);
        if (msg.event === "auth_ok") return;
        handleWsEvent(msg);
      } catch (_) {}
    };

    ws.onclose = (ev) => {
      if (wsRef.current !== ws) return;
      setWsConnected(false);
      if (ev.code === 4401) {
        clearTimeout(reconnectRef.current);
        setBanner("Session expired. Redirecting to login...");
        handleAuthFailure(ev.reason || "session_expired");
        return;
      }
      if (isMountedRef.current) {
        reconnectRef.current = setTimeout(connectWs, 3000);
      }
    };
  }, [tenantId, botId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    isMountedRef.current = true;
    connectWs();
    return () => {
      isMountedRef.current = false;
      clearTimeout(reconnectRef.current);
      const active = wsRef.current;
      wsRef.current = null;
      if (active && (active.readyState === WebSocket.OPEN || active.readyState === WebSocket.CONNECTING)) {
        active.close();
      }
    };
  }, [connectWs]);

  function handleWsEvent(msg) {
    setConversations((prev) => {
      const updated = { ...prev };
      const key = msg.session_key;
      if (!key) return prev;

      switch (msg.event) {
        case "user_message":
        case "bot_reply":
        case "agent_reply": {
          if (msg.channel === "whatsapp") {
            setChannelFilter("whatsapp");
            try {
              sessionStorage.setItem(`live_channel_${tenantId}_${botId}`, "whatsapp");
            } catch (_) {}
          }
          const entry = updated[key] || {
            phone: msg.phone || key,
            name: msg.name || "",
            channel: msg.channel || "whatsapp",
            mode: "ai",
            messages: [],
            historyLoaded: false,
            last_message: "",
            last_seen: null,
            unread: 0,
            has_pending: false,
          };
          const role =
            msg.event === "user_message"
              ? "user"
              : msg.event === "agent_reply"
              ? "agent_reply"
              : "bot";
          const newMsg = { role, text: msg.message, timestamp: msg.timestamp };
          // Deduplicate: skip if an identical message is already in the list
          const isDuplicate = entry.messages.some(
            (m) => m.role === role && m.text === msg.message &&
              Math.abs(new Date(m.timestamp) - new Date(msg.timestamp)) < 2000
          );
          updated[key] = {
            ...entry,
            name: msg.name || entry.name || "",
            phone: msg.phone || entry.phone || key,
            messages: !isDuplicate
              ? sortMessagesByTime([...entry.messages, newMsg])
              : entry.messages,
            last_message: msg.message,
            last_seen: msg.timestamp,
            unread:
              msg.event === "user_message" && key !== selectedKeyRef.current
                ? (entry.unread || 0) + 1
                : entry.unread || 0,
            has_pending: msg.event === "user_message",
          };
          break;
        }
        case "mode_changed":
          if (updated[key]) {
            updated[key] = { ...updated[key], mode: msg.mode };
          }
          break;
        case "session_ended":
          delete updated[key];
          if (key === selectedKey) setSelectedKey(null);
          break;
        default:
          break;
      }
      return updated;
    });
  }

  const fetchHistory = useCallback(async (key, mergeWithExisting = false) => {
    const gen = ++historyFetchGenRef.current;
    try {
      const res = await fetch(
        `${getApiBase()}/live/conversations/${encodeURIComponent(key)}/history`
      );
      if (res.status === 404) {
        try {
          sessionStorage.removeItem(storageKey);
        } catch (_) {}
        setSelectedKey((cur) => (cur === key ? null : cur));
        setConversations((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        return;
      }
      if (!res.ok || gen !== historyFetchGenRef.current) return;
      const history = await res.json();
      if (gen !== historyFetchGenRef.current) return;
      const fromApi = Array.isArray(history) ? history : [];
      setConversations((prev) => {
        const entry = prev[key] || {
          phone: key,
          channel: "web",
          mode: "ai",
          last_message: "",
          last_seen: null,
          unread: 0,
          has_pending: false,
          historyLoaded: false,
        };
        if (!prev[key] && fromApi.length === 0) {
          return prev;
        }
        const merged = mergeWithExisting
          ? mergeMessages(entry.messages || [], fromApi)
          : fromApi;
        return {
          ...prev,
          [key]: {
            ...entry,
            messages: merged,
            historyLoaded: true,
          },
        };
      });
    } catch (_) {}
  }, [storageKey]);

  const mergeListIntoConversations = useCallback((data, { restoreSelection = false } = {}) => {
    if (!Array.isArray(data)) return;

    let storedKey = null;
    if (restoreSelection) {
      try {
        storedKey = sessionStorage.getItem(storageKey);
      } catch (_) {}
    }

    const listKeys = data.map((c) => c.session_key);
    const keyInList = storedKey && listKeys.includes(storedKey) ? storedKey : null;

    const waPrefix = `wa_${tenantId}_${botId}_`;
    setConversations((prev) => {
      const next = { ...prev };
      const seen = new Set();
      data.forEach((c) => {
        seen.add(c.session_key);
        const existing = prev[c.session_key] || {};
        next[c.session_key] = {
          ...existing,
          messages: existing.messages || [],
          historyLoaded: existing.historyLoaded || false,
          unread: existing.unread || 0,
          phone: c.phone,
          name: c.name || existing.name || "",
          channel: c.channel,
          mode: c.mode,
          last_message: c.last_message || existing.last_message || "",
          last_seen: c.last_seen ?? existing.last_seen ?? null,
          has_pending: c.has_pending || false,
          unpaired_count: c.unpaired_count ?? 0,
        };
      });
      Object.keys(next).forEach((key) => {
        if (key.startsWith(waPrefix) && !seen.has(key)) {
          delete next[key];
        }
      });
      return next;
    });

    const hasWa = data.some((c) => c.channel === "whatsapp");
    const hasWeb = data.some((c) => c.channel === "web");
    if (hasWa && !hasWeb) {
      setChannelFilter("whatsapp");
    }

    if (restoreSelection) {
      if (!keyInList && storedKey) {
        try {
          sessionStorage.removeItem(storageKey);
        } catch (_) {}
      }
      if (keyInList) {
        setSelectedKey(keyInList);
        const ch = data.find((c) => c.session_key === keyInList)?.channel;
        if (ch) {
          setChannelFilter(ch);
          try {
            sessionStorage.setItem(`live_channel_${tenantId}_${botId}`, ch);
          } catch (_) {}
        }
        fetchHistory(keyInList, false);
      } else {
        setSelectedKey(null);
      }
    }
  }, [storageKey, tenantId, botId, fetchHistory]);

  const fetchConversationList = useCallback(async (restoreSelection = false) => {
    try {
      const listRes = await fetch(
        `${getApiBase()}/live/conversations/${tenantId}/${botId}`
      );
      if (!listRes.ok) return;
      const data = await listRes.json();
      mergeListIntoConversations(data, { restoreSelection });
    } catch (_) {}
  }, [tenantId, botId, mergeListIntoConversations]);

  // ── Initial load + restore history after route remount (e.g. Knowledge Base) ─
  useEffect(() => {
    fetchConversationList(true);
  }, [fetchConversationList]);

  // Poll list while Live tab is active (WA sessions are in-memory on the server)
  useEffect(() => {
    if (!isActive) return;
    const id = setInterval(() => fetchConversationList(false), 5000);
    return () => clearInterval(id);
  }, [isActive, fetchConversationList]);

  // ── Select conversation + load history ────────────────────────────────────
  async function selectConversation(key) {
    setSelectedKey(key);
    try {
      sessionStorage.setItem(storageKey, key);
    } catch (_) {}

    setConversations((prev) => ({
      ...prev,
      [key]: { ...prev[key], unread: 0 },
    }));

    await fetchHistory(key, false);
  }

  // Refresh when returning to Live tab (Overview) or page becomes visible again
  useEffect(() => {
    if (!isActive || !selectedKey) return;
    fetchHistory(selectedKey, false);
  }, [isActive, selectedKey, fetchHistory]);

  useEffect(() => {
    const ch = selectedKey ? conversations[selectedKey]?.channel : null;
    if (!ch) return;
    setChannelFilter(ch);
    try {
      sessionStorage.setItem(`live_channel_${tenantId}_${botId}`, ch);
    } catch (_) {}
  }, [selectedKey, conversations, tenantId, botId]);

  useEffect(() => {
    if (!isActive) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchConversationList(false);
        if (selectedKey) fetchHistory(selectedKey, false);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isActive, selectedKey, fetchHistory, fetchConversationList]);

  // Scroll the messages pane to the bottom when the selected conversation
  // gets new messages. We set scrollTop directly on the container instead of
  // using scrollIntoView (which walks up to the nearest scrollable ancestor
  // and can scroll the entire page when the chat pane isn't fully constrained).
  const selectedMessages = selectedKey
    ? conversations[selectedKey]?.messages
    : null;
  useEffect(() => {
    if (!messagesContainerRef.current) return;
    messagesContainerRef.current.scrollTop =
      messagesContainerRef.current.scrollHeight;
  }, [selectedMessages, selectedKey]);

  // ── Toggle mode ───────────────────────────────────────────────────────────
  async function toggleMode(key) {
    const conv = conversations[key];
    if (!conv) return;
    const newMode = conv.mode === "ai" ? "human" : "ai";
    setBanner(null);
    try {
      const res = await fetch(`${getApiBase()}/live/conversations/${encodeURIComponent(key)}/mode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: newMode }),
      });
      if (res.status === 409) {
        const err = await res.json().catch(() => ({}));
        const detail = err.detail;
        const msg =
          typeof detail === "object" && detail?.message
            ? detail.message
            : "Reply to all student messages before switching to AI.";
        const preview =
          (typeof detail === "object" && detail?.pending_preview) ||
          [...(conv.messages || [])].reverse().find((m) => m.role === "user")?.text;
        const text = preview
          ? `Reply before switching to AI: “${preview.slice(0, 200)}${preview.length > 200 ? "…" : ""}”`
          : msg;
        setBanner({ type: "error", text });
        return;
      }
      if (!res.ok) {
        setBanner({ type: "error", text: "Could not update mode. Try again." });
        return;
      }
      setConversations((prev) => ({
        ...prev,
        [key]: { ...prev[key], mode: newMode },
      }));
    } catch (_) {
      setBanner({ type: "error", text: "Could not update mode. Try again." });
    }
  }

  // ── Send human reply ──────────────────────────────────────────────────────
  async function sendReply() {
    if (!selectedKey || !replyText.trim() || sending) return;
    const conv = conversations[selectedKey];
    if (!conv || conv.mode !== "human") return;

    setSending(true);
    try {
      const res = await fetch(`${getApiBase()}/live/human-reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_key: selectedKey,
          message: replyText.trim(),
          channel: conv.channel,
        }),
      });
      if (res.ok) {
        const sent = replyText.trim();
        const ts = new Date().toISOString();
        setReplyText("");
        setConversations((prev) => {
          const entry = prev[selectedKey] || {};
          const optimistic = { role: "agent_reply", text: sent, timestamp: ts };
          return {
            ...prev,
            [selectedKey]: {
              ...entry,
              has_pending: false,
              unpaired_count: 0,
              messages: mergeMessages(entry.messages || [], [optimistic]),
            },
          };
        });
      } else {
        const err = await res.json().catch(() => ({}));
        const d = err.detail;
        setBanner({
          type: "error",
          text:
            typeof d === "string"
              ? d
              : d?.message || "Failed to send reply.",
        });
      }
    } catch (_) {
      setBanner({ type: "error", text: "Failed to send reply." });
    }
    setSending(false);
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const filteredKeys = Object.keys(conversations).filter(
    (k) => conversations[k].channel === channelFilter
  );
  const selectedConv = selectedKey ? conversations[selectedKey] : null;
  const canReply = selectedConv?.mode === "human";
  const canSend = canReply;

  return (
    <div className="flex flex-col h-full bg-white rounded-xl border border-[#E6DDD8] overflow-hidden">
      {banner && (
        <div
          className={`px-4 py-2 text-sm ${
            banner.type === "error"
              ? "bg-red-50 text-red-700 border-b border-red-100"
              : "bg-amber-50 text-amber-800 border-b border-amber-100"
          }`}
        >
          {banner.text}
          <button
            type="button"
            className="float-right text-xs opacity-60 hover:opacity-100"
            onClick={() => setBanner(null)}
          >
            Dismiss
          </button>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#E6DDD8]">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-gray-800">Live Conversations</h2>
          <span
            className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
              wsConnected
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-600"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${wsConnected ? "bg-green-500" : "bg-red-500 animate-pulse"}`}
            />
            {wsConnected ? "Live" : "Reconnecting..."}
          </span>
        </div>

        {/* Channel filter tabs */}
        <div className="flex gap-1 bg-[#F7F3F8] rounded-lg p-0.5">
          {Object.entries(CHANNEL_LABELS).map(([ch, label]) => (
            <button
              key={ch}
              onClick={() => {
                setChannelFilter(ch);
                try {
                  sessionStorage.setItem(`live_channel_${tenantId}_${botId}`, ch);
                } catch (_) {}
              }}
              className={`text-xs px-3 py-1 rounded-md transition-colors ${
                channelFilter === ch
                  ? "bg-white text-[#5B3A60] font-semibold shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Conversation list */}
        <div className="w-72 border-r border-[#E6DDD8] flex flex-col overflow-y-auto">
          {filteredKeys.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400 p-6 text-center">
              No active {CHANNEL_LABELS[channelFilter]} conversations
            </div>
          ) : (
            filteredKeys
              .sort((a, b) => {
                const la = conversations[a]?.last_seen || 0;
                const lb = conversations[b]?.last_seen || 0;
                return lb - la;
              })
              .map((key) => (
                <ConversationCard
                  key={key}
                  sessionKey={key}
                  conv={conversations[key]}
                  isSelected={key === selectedKey}
                  onSelect={selectConversation}
                />
              ))
          )}
        </div>

        {/* Chat pane */}
        <div className="flex-1 flex flex-col min-h-0">
          {!selectedConv ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
              Select a conversation to view
            </div>
          ) : (
            <>
              {/* Chat pane header */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E6DDD8] bg-gray-50">
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {selectedConv.channel === "whatsapp" ? "📱 " : "💻 "}
                    {selectedConv.channel === "whatsapp"
                      ? (selectedConv.name || selectedConv.phone || selectedKey)
                      : (selectedConv.phone || selectedKey)}
                  </p>
                  {selectedConv.channel === "whatsapp" && selectedConv.name && selectedConv.phone && (
                    <p className="text-xs text-gray-500">{selectedConv.phone}</p>
                  )}
                  <p className="text-xs text-gray-500 capitalize">
                    {selectedConv.channel} · {selectedConv.mode === "human" ? "Human mode" : "AI mode"}
                  </p>
                </div>

                {/* Mode toggle button */}
                <button
                  onClick={() => toggleMode(selectedKey)}
                  className={`flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${
                    selectedConv.mode === "human"
                      ? "bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100"
                      : "bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${
                      selectedConv.mode === "human" ? "bg-orange-400" : "bg-green-400"
                    }`}
                  />
                  {selectedConv.mode === "human" ? "Human — switch to AI" : "AI — switch to Human"}
                </button>
              </div>

              {/* Messages — ref is on the scrollable container itself */}
              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 py-3">
                {!selectedConv.historyLoaded && (
                  <p className="text-center text-xs text-gray-400 mb-3">Loading history...</p>
                )}
                {selectedConv.historyLoaded && !(selectedConv.messages || []).length && (
                  <p className="text-center text-xs text-gray-400 mb-3">No messages yet</p>
                )}
                {(selectedConv.messages || []).map((msg, i) => (
                  <MessageBubble key={`${msg.role}-${messageTimeMs(msg.timestamp)}-${i}`} msg={msg} />
                ))}
                {selectedConv.mode === "human" && selectedConv.has_pending && (
                  <p className="text-center text-xs text-orange-500 animate-pulse mt-2">
                    {selectedConv.unpaired_count > 1
                      ? `${selectedConv.unpaired_count} student messages waiting for reply`
                      : "Student message waiting for your reply"}
                  </p>
                )}
              </div>

              {/* Reply composer */}
              <div className="border-t border-[#E6DDD8] px-4 py-3">
                {!canReply ? (
                  <p className="text-xs text-gray-400 text-center py-1">
                    AI is handling this conversation — toggle to Human to reply manually
                  </p>
                ) : (
                  <div className="flex gap-2 items-end">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendReply();
                        }
                      }}
                      placeholder="Type reply and press Enter..."
                      rows={2}
                      className="flex-1 resize-none border border-[#E6DDD8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#5B3A60] placeholder-gray-400"
                    />
                    <button
                      onClick={sendReply}
                      disabled={!replyText.trim() || sending}
                      className="px-4 py-2 rounded-lg bg-[#5B3A60] text-white text-sm font-medium disabled:opacity-40 hover:bg-[#4a2e4e] transition-colors"
                    >
                      {sending ? "..." : "Send"}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
